/**
 * Cadrage mobile (`description.md:19-29`) : arrêt par **palier** sur un écran qui tient dans le
 * petit viewport. Le palier est du CSS (`position:sticky` imbriqué, règles K1-K5 de
 * `styles/mobile.ts`) ; ce module ne connaît ni l'écran ni le défilement.
 *
 * La respiration (espace libre du cadrage, `LIBRE`) est partagée à parts égales entre la marge haute des
 * textes et celle de la synthèse, écrites par la feuille (K3) : les textes sont centrés entre la légende et
 * la synthèse (`description.md:20`).
 *
 * Un cadrage peut compter plusieurs écrans (porteurs, `CadrageSpec.frame`, `scene-manifest.ts`) : chacun est mesuré
 * par ce même modèle et reçoit ses propres variables.
 *
 * K5 translate la synthèse de la hauteur libérée par la barre du navigateur (option A) ou d'un décalage
 * fixe (repli B), selon `CADRAGE_FOLLOW_BAR`, et les textes de la moitié, pour qu'ils restent centrés ;
 * sans effet sur la mise en page ni sur ce modèle.
 *
 * Consommateurs : `styles/mobile.ts` (constantes, noms de variables), `engine/cadrage-bind.ts` (calcul des
 * mesures), `engine/opening.ts` (`CADRAGE_GAP_TOP`) et `scenes/cockpit.opening.ts` (`CADRAGE_VARS.off`) pour la
 * fin du fondu du décollage.
 *
 * Les réglages sont ici seulement. Les chiffres du plan sont des mesures Chrome sans interface et
 * des **estimations** de `svh`, à confirmer sur appareil (plan-cadrage §2 bis et §5, R-C3).
 */

/**
 * Longueur du palier en `svh` : « de l'ordre d'un tiers d'écran » (`description.md:21`).
 * Valeur décidée par le plan (§5 point 6), estimation : à ajuster après test sur appareil.
 */
export const CADRAGE_HOLD_SVH = 33;

/**
 * Pixels entre le bas de l'en-tête et le haut de la figure. La rampe `.hdr::before` voile les
 * 20 px sous l'en-tête (`styles/header.css:17-20`) : à +12 px, le bord du cadre est sous un voile
 * d'environ 0,4 et le titre « Maison B. » reste net. Invariant : `CADRAGE_GAP_TOP + 4 ≤` gap de
 * grille (16 au téléphone, 24 sur iPad), pour que le bloc hero et l'ombre du bouton
 * (`0 1px 3px`, `styles/header.css:103`) restent sous l'en-tête opaque (testé en mission 03, S15).
 * Réglage R-C3 : iPhone 390/393 px à la limite du budget (de −6 à +6 px) ; à ajuster après test
 * sur appareil.
 * Lu aussi par `takeoffLength` (`engine/opening.ts`), avec le même sens : fin du fondu du décollage, quand `.in` colle (K2).
 */
export const CADRAGE_GAP_TOP = 12;

/**
 * Pixels d'air, **dans la mise en page**, entre le bas de la synthèse et le bas du petit viewport
 * (`100svh`), seul bas de mise en page sûr sur tous les navigateurs (recherche Q3,
 * `docs/research/2026-10-05_safari-ios-viewport-vitrine-mobile.md:71`). La règle « tient » et la
 * respiration sont calées sur ce bas (`svh`, barre agrandie).
 * En option A (`CADRAGE_FOLLOW_BAR`), K5 descend la synthèse de la hauteur libérée par la barre
 * (`100dvh − 100svh`), et les textes de la moitié : la synthèse est à 8 px du bas **visible**, quelle que soit la barre.
 * En repli B, K5 la descend de `CADRAGE_FIXED_DROP` : 8 − 30 = −22 px sous le bas de `svh`, donc sous la
 * barre agrandie (R-N4). 8 px gardent le jambage de « p » (≈ 1 px sous la boîte de ligne à 29 px / 1,05)
 * hors du bord. Barre compacte d'iOS 26 : `100svh` ≈ 714 px sur iPhone 17 Pro et ≈ 41-47 px libérés,
 * valeurs déduites de captures, non instrumentées (plan-cadrage-2 §2 bis C). Réglage R-A2.
 */
export const CADRAGE_GAP_BOTTOM = 8;

/**
 * Bascule A/B du bas du cadrage (`description.md:22-23`), lue par les règles K5t/K5s de `styles/mobile.ts`.
 * - `true` = option A, choisie par l'utilisateur : synthèse translatée de `100dvh − 100svh` (hauteur libérée par
 *   la barre du navigateur), textes de la moitié, avec un même glissement CSS : les textes restent centrés ;
 * - `false` = repli B, à n'activer que si le glissement est jugé saccadé sur appareil : synthèse translatée de
 *   `CADRAGE_FIXED_DROP`, textes de la moitié, sans glissement. **En B, la synthèse passe sous la barre agrandie** (R-N4).
 * Seule constante à changer pour basculer, puis relancer `styles/mobile.test.ts`. Les tests couvrent
 * les deux valeurs sans épingler celle-ci. Annotée `boolean` pour que les deux branches restent typées
 * chez les consommateurs.
 */
export const CADRAGE_FOLLOW_BAR: boolean = true;

/**
 * Décalage du repli B, en px sous la position « barre agrandie » (« environ 30 px », `description.md:23`) :
 * la synthèse descend de cette valeur, les textes de la moitié (15 px). Sans effet en option A.
 */
export const CADRAGE_FIXED_DROP = 30;

/**
 * Noms des variables CSS écrites sur le porteur (`.pin`, ou chaque `frame.holder`) par `engine/cadrage-bind.ts` (écrivain unique) et lues par K1-K3 et K5
 * (marche « tient ») : `off` = décalage de la figure dans `.in`, lu aussi par l'ouverture du cockpit
 * (`scenes/cockpit.opening.ts`, fin du fondu du décollage, même repli 0 que K2) ; `h` = hauteur du cadrage, du haut
 * de la figure au bas de la synthèse, **respiration exclue** (marges hautes des textes et de la synthèse, K3).
 */
export const CADRAGE_VARS = { off: "--cad-off", h: "--cad-h" } as const;

/**
 * Mise en page mesurée, en px de **mise en page** relatifs à l'élément collant `.in`
 * (insensibles aux `transform`).
 */
export type CadrageLayout = {
  /** Haut de la figure dans `.in`. */
  readonly figureTop: number;
  /** Haut de la boîte de bordure des textes dans `.in` (la respiration est au-dessus). Sans textes (`CadrageSpec.texts` absent), la liaison passe `textsTop = synthesisTop` et `textsMargin = 0`. */
  readonly textsTop: number;
  /** Marge haute calculée des textes : première moitié de la respiration, posée par K3. Sans textes (`CadrageSpec.texts` absent), la liaison passe `textsTop = synthesisTop` et `textsMargin = 0`. */
  readonly textsMargin: number;
  /** Haut de la boîte de bordure de la synthèse dans `.in`. */
  readonly synthesisTop: number;
  /** Marge haute calculée de la synthèse : seconde moitié de la respiration, posée par K3. */
  readonly synthesisMargin: number;
  /** Hauteur de la synthèse (`offsetHeight`). */
  readonly synthesisHeight: number;
};

/** Les deux mesures de contenu du cadrage, valeurs des variables `CADRAGE_VARS`. */
export type CadrageMetrics = { readonly off: number; readonly h: number };

/**
 * Calcule `off` (décalage de la figure) et `h` (hauteur du cadrage, du haut de la figure au bas
 * de la synthèse).
 *
 * La respiration (marges hautes des textes **et** de la synthèse, les deux moitiés posées par K3) est **exclue**
 * de `h` : K3 l'écrit à partir de `h`, donc l'inclure bouclerait (avec la seule seconde moitié incluse, `LIBRE`
 * convergerait vers 2/3 de sa valeur, plan-centrage R-6a). Aucun arrondi. `null` aussi si l'ordre
 * figure → textes → synthèse n'est pas respecté (R-A8 : repli plutôt qu'un palier faux).
 *
 * @param l mise en page mesurée
 * @returns les mesures, ou `null` si aucune mise en page exploitable (jsdom, `display:none`,
 * valeur non finie, `off < 0` ou `h ≤ 0`) : l'appelant n'écrit rien.
 */
export function cadrageMetrics(l: CadrageLayout): CadrageMetrics | null {
  const { figureTop, textsTop, textsMargin, synthesisTop, synthesisMargin, synthesisHeight } = l;
  if (![figureTop, textsTop, textsMargin, synthesisTop, synthesisMargin, synthesisHeight].every(Number.isFinite)) return null;
  if (!(figureTop < textsTop && textsTop <= synthesisTop)) return null; // déclaration incohérente : repli
  const off = figureTop;
  const h = synthesisTop + synthesisHeight - figureTop - textsMargin - synthesisMargin;
  return off < 0 || h <= 0 ? null : { off, h };
}
